import React, { useMemo, useState } from "react";

import { CabinetEmpty, CabinetSkeleton } from "../components/CabinetStates";
import { formatDate, formatDateTime, formatNumber } from "../format";
import {
  buildPlaceViews,
  contractLabelOf,
  currentShareOf,
  currentTextOf,
  placesSummaryOf,
  stationStatusOf,
} from "../stationsView";
import type { PlaceView } from "../stationsView";
import { SUPPORT_LINK } from "../support";
import { useCabinetStations } from "../useCabinetStations";

/**
 * `Ф1` (кабинет ПЗС): раздел «Станции».
 *
 * Слева (на телефоне — вкладками сверху) места, справа — панель выбранного
 * места: состояние, ток сейчас и лимит, показание и время, реквизиты. Данные —
 * `GET /myStations` и `GET /my/places`, тихое обновление раз в 15 с.
 *
 * Блоков «этап 2» здесь нет: «за сегодня», графиков за 24 часа и карт доступа.
 */

function chipLabelOf(view: PlaceView): string {
  if (view.kind === "station" && view.station) {
    return stationStatusOf(view.station).label;
  }

  switch (view.kind) {
    case "waiting-act":
      return "ждёт акта";
    case "pending":
      return "связь не началась";
    case "closed":
      return "связь закрыта";
    default:
      return "—";
  }
}

function StationPanel({ view }: { view: PlaceView }) {
  const number = view.number ?? "—";
  const address = view.place?.place?.plant?.address ?? null;

  if (view.kind === "waiting-act") {
    return (
      <div className="cabinet-station-panel">
        <PanelHead number={number} plantName={view.plantName} address={address} contract={view.place?.contract ?? null} />
        <CabinetEmpty title={`Место ${number} ждёт акта установки станции`}>
          Данные станции появятся после подписания акта. Обычно это делает
          монтажная организация.
        </CabinetEmpty>
      </div>
    );
  }

  if (view.kind === "pending") {
    return (
      <div className="cabinet-station-panel">
        <PanelHead number={number} plantName={view.plantName} address={address} contract={view.place?.contract ?? null} />
        <CabinetEmpty title="Связь с местом ещё не началась">
          Начало связи — {formatDate(view.place?.started_at)}. Данные станции
          появятся, когда связь вступит в силу.
        </CabinetEmpty>
      </div>
    );
  }

  if (view.kind === "closed") {
    return (
      <div className="cabinet-station-panel">
        <PanelHead number={number} plantName={view.plantName} address={address} contract={view.place?.contract ?? null} />
        <CabinetEmpty title="Связь с местом закрыта">
          {/* `E.3`: фраза дословно по `АРХ-ЛК-20.10` (стр. 92). */}
          История за период связи — в разделе «Потребление» до{" "}
          {formatDate(view.place?.history_until)}. Показания старше года не
          хранятся.
        </CabinetEmpty>
      </div>
    );
  }

  const station = view.station;

  if (!station) {
    return (
      <div className="cabinet-station-panel">
        <PanelHead number={number} plantName={view.plantName} address={address} contract={view.place?.contract ?? null} />
        <CabinetEmpty title="Станция не подключена" />
      </div>
    );
  }

  const status = stationStatusOf(station);
  // `E.2`: ширина полосы — доля тока от лимита; нет чисел — полосы нет.
  const share = currentShareOf(station);

  return (
    <div className="cabinet-station-panel">
      <PanelHead number={number} plantName={view.plantName} address={address} contract={view.place?.contract ?? null} />

      <div className="cabinet-status-hero" style={{ color: status.color }}>
        <span className="cabinet-status-hero__caption">Станция</span>
        <b className="cabinet-status-hero__label">{status.label}</b>
        {status.hint ? (
          <span className="cabinet-status-hero__hint">{status.hint}</span>
        ) : null}
      </div>

      {share !== null ? (
        <div
          className="cabinet-current-bar"
          role="img"
          aria-label={`Ток ${currentTextOf(station)} из ${formatNumber(station.limit_current)} А`}
        >
          <span
            className="cabinet-current-bar__fill"
            data-share={share.toFixed(3)}
            style={{ width: `${Math.round(share * 100)}%` }}
          />
        </div>
      ) : null}

      <div className="cabinet-metrics">
        <div className="cabinet-metric">
          <span className="cabinet-metric__label">Ток сейчас</span>
          <b className="cabinet-metric__value cabinet-num">
            {currentTextOf(station)} А
          </b>
        </div>
        <div className="cabinet-metric">
          <span className="cabinet-metric__label">Лимит</span>
          <b className="cabinet-metric__value cabinet-num">
            {formatNumber(station.limit_current)} А
          </b>
        </div>
        <div className="cabinet-metric">
          <span className="cabinet-metric__label">Показание счётчика</span>
          <b className="cabinet-metric__value cabinet-num">
            {formatNumber(station.meter?.last_reading_kwh)} кВт·ч
          </b>
          <span className="cabinet-metric__note cabinet-num">
            на {formatDateTime(station.meter?.last_reading_at)}
          </span>
        </div>
      </div>

      <dl className="cabinet-fields">
        <div className="cabinet-fields__row">
          <dt>Серийный №</dt>
          <dd className="cabinet-num">{station.serial ?? "—"}</dd>
        </div>
        <div className="cabinet-fields__row">
          <dt>Счётчик №</dt>
          <dd className="cabinet-num">{station.meter?.number ?? "—"}</dd>
        </div>
        <div className="cabinet-fields__row">
          <dt>Объект</dt>
          <dd>{station.plant?.name ?? "—"}</dd>
        </div>
        <div className="cabinet-fields__row">
          <dt>Связь с местом с</dt>
          <dd className="cabinet-num">
            {formatDate(station.place?.link_started_at)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function PanelHead({
  number,
  plantName,
  address,
  contract,
}: {
  number: string;
  plantName: string | null;
  address: string | null;
  contract: { number: string | null; date: string | null } | null;
}) {
  return (
    <div className="cabinet-station-panel__head">
      <h2>Место {number}</h2>
      <span>
        {plantName ?? "—"}
        {address ? ` · ${address}` : ""}
      </span>
      {/* `E.1`: договор — в карточке места; без него прочерк. */}
      <span className="cabinet-hint cabinet-station-panel__contract">
        {contractLabelOf(contract)}
      </span>
    </div>
  );
}

export default function CabinetStationsPage() {
  const { loading, error, stations, places, reload } = useCabinetStations();
  const views = useMemo(
    () => buildPlaceViews(stations, places),
    [stations, places],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Выбранное место выводится, а не хранится: если данные обновились и места
  // больше нет, показывается первое — без гонки «эффект переписал выбор».
  const activeKey =
    selectedKey && views.some((view) => view.key === selectedKey)
      ? selectedKey
      : views[0]?.key ?? null;
  const selected = views.find((view) => view.key === activeKey) ?? null;

  return (
    <section className="cabinet-page" aria-labelledby="cabinet-stations-title">
      <header className="cabinet-page__head">
        <h1 id="cabinet-stations-title">Станции</h1>
        <p>
          {views.length > 0
            ? placesSummaryOf(views)
            : "Мои машиноместа и станции на них"}
        </p>
      </header>

      {loading && views.length === 0 ? <CabinetSkeleton /> : null}

      {!loading && error && views.length === 0 ? (
        <CabinetEmpty title="Не удалось загрузить станции">
          <button type="button" className="cabinet-btn" onClick={reload}>
            Обновить
          </button>
        </CabinetEmpty>
      ) : null}

      {!loading && !error && views.length === 0 ? (
        <CabinetEmpty title="Машиномест пока нет">
          Места появляются после заключения договора. Если договор есть —{" "}
          <a href={SUPPORT_LINK}>напишите в поддержку</a>.
        </CabinetEmpty>
      ) : null}

      {views.length > 0 ? (
        <div className="cabinet-stations">
          <div
            className="cabinet-stations__list"
            role="tablist"
            aria-label="Мои машиноместа"
          >
            {views.map((view) => {
              const active = view.key === activeKey;

              return (
                <button
                  key={view.key}
                  type="button"
                  role="tab"
                  id={`cabinet-place-tab-${view.placeId}`}
                  aria-selected={active}
                  aria-controls={`cabinet-place-panel-${view.placeId}`}
                  className={`cabinet-place${active ? " cabinet-place--active" : ""}`}
                  onClick={() => setSelectedKey(view.key)}
                >
                  <span className="cabinet-place__top">
                    <b>Место {view.number ?? "—"}</b>
                    <span className="cabinet-place__chip">
                      {chipLabelOf(view)}
                    </span>
                  </span>
                  <span className="cabinet-place__plant">
                    {view.plantName ?? "—"}
                  </span>
                  <span className="cabinet-place__meta">
                    {view.station ? (
                      <>
                        Ток{" "}
                        <b className="cabinet-num">
                          {currentTextOf(view.station)} А
                        </b>{" "}
                        · Счётчик{" "}
                        <b className="cabinet-num">
                          {view.station.meter?.number ?? "—"}
                        </b>
                      </>
                    ) : view.kind === "closed" ? (
                      <>История до {formatDate(view.place?.history_until)}</>
                    ) : (
                      "Данные станции появятся после акта"
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {selected ? (
            <section
              id={`cabinet-place-panel-${selected.placeId}`}
              role="tabpanel"
              aria-labelledby={`cabinet-place-tab-${selected.placeId}`}
              className="cabinet-stations__panel"
            >
              <StationPanel view={selected} />
            </section>
          ) : null}

          {/* `E.2` (макет `Main.dc.html`): «нет нужного места — напишите в поддержку». */}
          <p className="cabinet-stations__support">
            Нет нужного места? <a href={SUPPORT_LINK}>Напишите в поддержку</a>.
          </p>
        </div>
      ) : null}
    </section>
  );
}
